<template>
  <div class="raw-json-section">
    <div class="raw-json-header" @click="toggleRawJson">
      <h5>Raw JSON Payload</h5>
      <span class="toggle-icon" :class="{ 'expanded': showRawJson }">
        <i class="fas fa-chevron-down"></i>
      </span>
    </div>
    <div v-if="showRawJson" class="raw-json-content">
      <div class="json-toolbar">
        <button @click="copyRawJson" class="copy-btn">
          <i class="fas fa-copy"></i>
          Copy JSON
        </button>
        <span v-if="copySuccess" class="copy-success">
          <i class="fas fa-check"></i>
          Copied!
        </span>
      </div>
      <pre class="raw-json-display">{{ prettifiedJson }}</pre>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RawJsonDisplay',
  props: {
    cycleDetails: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showRawJson: false,
      copySuccess: false
    };
  },
  computed: {
    prettifiedJson() {
      if (!this.cycleDetails) return '';
      // Use JSON.stringify with proper indentation and handle circular references
      try {
        return JSON.stringify(this.cycleDetails, null, 2);
      } catch (error) {
        console.warn('Failed to stringify cycle details:', error);
        return '{}';
      }
    }
  },
  methods: {
    toggleRawJson() {
      this.showRawJson = !this.showRawJson;
    },
    async copyRawJson() {
      try {
        await navigator.clipboard.writeText(this.prettifiedJson);
        this.copySuccess = true;
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy JSON:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.prettifiedJson;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.copySuccess = true;
        setTimeout(() => {
          this.copySuccess = false;
        }, 2000);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.raw-json-section {
  margin-top: 24px;
  border: 1px solid rgb(58, 74, 107);
  border-radius: 12px;
  overflow: hidden;
  background: rgb(24, 32, 64);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

  .raw-json-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background: rgb(32, 40, 80);
    border-bottom: 1px solid rgb(58, 74, 107);
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background: rgb(42, 58, 107);
    }

    h5 {
      margin: 0;
      color: #ffffff;
      font-size: 16px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;

      &::before {
        content: '📄';
        font-size: 14px;
      }
    }

    .toggle-icon {
      transition: transform 0.3s ease;
      color: #8fa3b8;
      font-size: 16px;

      &.expanded {
        transform: rotate(180deg);
      }
    }
  }

  .raw-json-content {
    .json-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 20px;
      background: rgb(32, 40, 80);
      border-bottom: 1px solid rgb(58, 74, 107);

      .copy-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &:hover {
          background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
        }

        i {
          font-size: 11px;
        }
      }

      .copy-success {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #2e7d32;
        font-size: 12px;
        font-weight: 500;

        i {
          font-size: 11px;
        }
      }
    }

    .raw-json-display {
      margin: 0;
      padding: 20px;
      background: rgb(16, 22, 48);
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 12px;
      line-height: 1.5;
      color: #ffffff;
      overflow-x: auto;
      white-space: pre-wrap;
      word-wrap: break-word;
      max-height: 400px;
      overflow-y: auto;
      border: none;

      /* JSON syntax highlighting */
      .json-key {
        color: #0066cc;
        font-weight: 600;
      }

      .json-string {
        color: #008000;
      }

      .json-number {
        color: #cc6600;
      }

      .json-boolean {
        color: #9900cc;
        font-weight: 600;
      }

      .json-null {
        color: #999999;
        font-style: italic;
      }
    }
  }
}
</style>
